/**
 * HallucinationReportSection
 *
 * Displays hallucination validation results comparing generated content
 * claims against source product feed data.
 *
 * Part of Story 49: Detect Spec Hallucinations
 *
 * Date: December 23, 2025
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertCircle, CheckCircle, AlertTriangle, Loader2, RefreshCw, Info } from 'lucide-react';
import {
  HallucinationReport,
  validateSpecs,
  getHallucinationReport,
  getHallucinationSeverityColor,
  getHallucinationDecisionColor,
} from '@/services/contentReviewService';

interface HallucinationReportSectionProps {
  generationId: string;
  autoRun?: boolean;
}

export function HallucinationReportSection({ generationId, autoRun = false }: HallucinationReportSectionProps) {
  const [report, setReport] = useState<HallucinationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);

  // Load existing report on mount
  useEffect(() => {
    async function loadExistingReport() {
      try {
        const response = await getHallucinationReport(generationId);
        if (response.data) {
          setReport(response.data);
          setHasChecked(true);
        } else if (autoRun) {
          // Auto-run validation if no existing report
          handleRunValidation();
        }
      } catch (err) {
        console.error('Error loading hallucination report:', err);
        if (autoRun) {
          handleRunValidation();
        }
      }
    }

    loadExistingReport();
  }, [generationId, autoRun]);

  async function handleRunValidation() {
    setLoading(true);
    setError(null);

    try {
      const response = await validateSpecs(generationId);
      if (response.data) {
        setReport(response.data);
        setHasChecked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run validation');
    } finally {
      setLoading(false);
    }
  }

  // Render decision icon
  const DecisionIcon = () => {
    if (!report) return null;

    switch (report.decision) {
      case 'PASS':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'FLAG':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'BLOCK':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  // If no check has been run yet
  if (!hasChecked && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Spec Hallucination Check
          </CardTitle>
          <CardDescription>
            Compare generated content claims against source product feed data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRunValidation} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              'Run Spec Validation'
            )}
          </Button>
          {error && (
            <p className="mt-2 text-sm text-red-500">{error}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Validating Specs...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Extracting claims from content and comparing against product feed...
          </p>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && !report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-500">
            <AlertCircle className="h-5 w-5" />
            Validation Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Report display
  if (!report) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DecisionIcon />
            Spec Hallucination Check
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={getHallucinationDecisionColor(report.decision)}>
              {report.decision}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunValidation}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>{report.summary}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 text-center">
          <div className="space-y-1">
            <p className="text-2xl font-bold">{report.stats.totalClaims}</p>
            <p className="text-xs text-muted-foreground">Claims Extracted</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-green-600">{report.stats.verified}</p>
            <p className="text-xs text-muted-foreground">Verified</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-red-600">{report.stats.discrepancies}</p>
            <p className="text-xs text-muted-foreground">Discrepancies</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-gray-500">{report.stats.unverifiable}</p>
            <p className="text-xs text-muted-foreground">Unverifiable</p>
          </div>
        </div>

        {/* Discrepancies */}
        {report.discrepancies.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              Discrepancies Found
            </h4>
            <div className="space-y-2">
              {report.discrepancies.map((disc, index) => (
                <div
                  key={index}
                  className="p-3 border rounded-lg bg-red-50 dark:bg-red-950/20"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {disc.attribute}
                        </span>
                        <Badge variant={getHallucinationSeverityColor(disc.severity)} className="text-xs">
                          {disc.severity}
                        </Badge>
                      </div>
                      <div className="text-sm">
                        <span className="text-red-600">Claimed: {disc.claimed}</span>
                        <span className="mx-2 text-muted-foreground">vs</span>
                        <span className="text-green-600">Actual: {disc.actual}</span>
                      </div>
                      {disc.location && (
                        <p className="text-xs text-muted-foreground">
                          Location: {disc.location}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Verified Claims (collapsed by default) */}
        {report.verified && report.verified.length > 0 && (
          <details className="space-y-2">
            <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Verified Claims ({report.verified.length})
            </summary>
            <div className="mt-2 space-y-1 pl-6">
              {report.verified.map((claim, index) => (
                <div
                  key={index}
                  className="text-sm text-muted-foreground"
                >
                  <span className="font-mono">{claim.attribute}</span>: {claim.value}{claim.unit || ''}
                  <span className="text-xs ml-2">({claim.matchType})</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Unverifiable Claims (collapsed by default) */}
        {report.unverifiable && report.unverifiable.length > 0 && (
          <details className="space-y-2">
            <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
              <Info className="h-4 w-4 text-gray-500" />
              Unverifiable Claims ({report.unverifiable.length})
            </summary>
            <div className="mt-2 space-y-1 pl-6">
              {report.unverifiable.map((claim, index) => (
                <div
                  key={index}
                  className="text-sm text-muted-foreground"
                >
                  <span className="font-mono">{claim.attribute}</span>: {claim.value}{claim.unit || ''}
                  <span className="text-xs ml-2 text-yellow-600">({claim.reason})</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          Checked: {new Date(report.checkedAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}
